import type { CSSProperties, ReactEventHandler } from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  defaultSrc,
  optimizeRemoteImageSrcSet,
  optimizeRemoteImageUrl,
  type ResponsiveImageSpec,
  webpSrcSet,
} from '@/shared/utils/imageAssets'
import { observeWhenReady } from '@/shared/hooks/useFetchWhenNearViewport'
export type OptimizedPictureProps = {
  spec?: ResponsiveImageSpec
  /** Override or use instead of spec */
  src?: string
  widths?: number[]
  sizes?: string
  alt: string
  className?: string
  style?: CSSProperties
  loading?: 'lazy' | 'eager'
  fetchPriority?: 'high' | 'low' | 'auto'
  width?: number
  height?: number
  onError?: ReactEventHandler<HTMLImageElement>
  onLoad?: ReactEventHandler<HTMLImageElement>
  /** Do not request the image until the element nears the viewport. */
  deferUntilVisible?: boolean
}

function isRemote(url: string): boolean {
  return /^https?:\/\//i.test(url)
}

export function OptimizedPicture({
  spec,
  src: srcProp,
  widths: widthsProp,
  sizes: sizesProp,
  alt,
  className,
  style,
  loading = 'lazy',
  fetchPriority,
  width: widthProp,
  height: heightProp,
  onError,
  onLoad,
  deferUntilVisible = false,
}: OptimizedPictureProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(!deferUntilVisible)

  useEffect(() => {
    if (!deferUntilVisible) return
    return observeWhenReady(containerRef, () => setIsVisible(true), { rootMargin: '480px' })
  }, [deferUntilVisible])

  const src = srcProp ?? spec?.src ?? ''
  const widths = widthsProp ?? spec?.widths ?? []
  const sizes = sizesProp ?? spec?.sizes ?? '100vw'
  const intrinsicWidth = widthProp ?? spec?.width
  const intrinsicHeight = heightProp ?? spec?.height
  const isLcp = fetchPriority === 'high' && loading === 'eager'
  const reserveSpaceStyle: CSSProperties | undefined =
    deferUntilVisible && intrinsicWidth && intrinsicHeight
      ? { aspectRatio: `${intrinsicWidth} / ${intrinsicHeight}` }
      : undefined

  if (!src) return null

  if (deferUntilVisible && !isVisible) {
    return <div ref={containerRef} className={className} style={{ ...reserveSpaceStyle, ...style }} aria-hidden />
  }

  const renderImage = () => {
    if (isRemote(src)) {
      const optimized = optimizeRemoteImageUrl(src, 400)
      const remoteSet = optimizeRemoteImageSrcSet(src, [384, 768])
      return (
        <img
          src={optimized}
          srcSet={remoteSet || undefined}
          sizes={remoteSet ? '(max-width: 768px) 384px, 768px' : undefined}
          alt={alt}
          className={className}
          style={style}
          loading={loading}
          fetchPriority={fetchPriority}
          onError={onError}
          onLoad={onLoad}
          decoding={isLcp ? 'sync' : 'async'}
          width={intrinsicWidth}
          height={intrinsicHeight}
        />
      )
    }

    const webpSet = widths.length ? webpSrcSet(src, widths) : ''
    const imgSrc = widths.length ? defaultSrc(src, widths) : null

    if (!imgSrc) {
      console.warn(`OptimizedPicture: add widths/spec for local image ${src}`)
      return null
    }

    // Keep <picture> as the positioned frame when callers pass absolute/inset fill
    // classes. Do not use display:contents on <picture> (browser bugs escape layout).
    const fillsFrame =
      Boolean(className?.includes('absolute') || className?.includes('inset-0')) ||
      (typeof style?.height === 'string' && style.height !== 'auto' && style.height !== undefined)

    if (fillsFrame) {
      return (
        <picture
          className={className}
          style={{ display: 'block', width: '100%', height: '100%', ...style }}
        >
          {webpSet ? (
            <source type="image/webp" srcSet={webpSet} sizes={sizes} />
          ) : null}
          <img
            src={imgSrc}
            alt={alt}
            className="h-full w-full object-cover object-center"
            style={{ display: 'block', width: '100%', height: '100%' }}
            loading={loading}
            fetchPriority={fetchPriority}
            onError={onError}
            onLoad={onLoad}
            decoding={isLcp ? 'sync' : 'async'}
            width={intrinsicWidth}
            height={intrinsicHeight}
          />
        </picture>
      )
    }

    return (
      <picture>
        {webpSet ? (
          <source type="image/webp" srcSet={webpSet} sizes={sizes} />
        ) : null}
        <img
          src={imgSrc}
          alt={alt}
          className={className}
          style={style}
          loading={loading}
          fetchPriority={fetchPriority}
          onError={onError}
          onLoad={onLoad}
          decoding={isLcp ? 'sync' : 'async'}
          width={intrinsicWidth}
          height={intrinsicHeight}
        />
      </picture>
    )
  }

  if (deferUntilVisible) {
    return (
      <div ref={containerRef}>
        {renderImage()}
      </div>
    )
  }

  return renderImage()
}
